import { Dialog, Transition } from '@headlessui/react';
import { Fragment, useMemo } from 'react';
import { Button } from '@library/components/buttons';
import { classNames } from '@library/utils';

export interface DrawerProps {
  readonly children: React.ReactNode;
  readonly direction: 'left' | 'right';
  readonly isOpen: boolean;
  readonly onCloseDrawer: () => void;
  readonly size?: 'small' | 'medium' | 'large' | 'xlarge';
}

export function Drawer({
  children,
  direction,
  isOpen,
  onCloseDrawer,
  size = 'medium'
}: DrawerProps) {
  const sizeClasses = useMemo(() => {
    const classes = {
      width: '',
      maxWidth: '',
      translation: ''
    };
    switch (size) {
      case 'small':
        classes.width = 'w-[20rem]';
        classes.maxWidth = 'max-w-[20rem]';
        classes.translation =
          direction === 'left' ? `translate-x-[-20rem]` : `translate-x-[20rem]`;
        break;
      case 'medium':
        classes.width = 'w-[30rem]';
        classes.maxWidth = 'max-w-[30rem]';
        classes.translation =
          direction === 'left' ? `translate-x-[-30rem]` : `translate-x-[30rem]`;
        break;
      case 'large':
        classes.width = 'w-[40rem]';
        classes.maxWidth = 'max-w-[40rem]';
        classes.translation =
          direction === 'left' ? `translate-x-[-40rem]` : `translate-x-[40rem]`;
        break;
      case 'xlarge':
        classes.width = 'w-[50rem]';
        classes.maxWidth = 'max-w-[50rem]';
        classes.translation =
          direction === 'left' ? `translate-x-[-50rem]` : `translate-x-[50rem]`;
        break;
      default:
    }
    return classes;
  }, [direction, size]);

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog onClose={onCloseDrawer} className="relative z-50">
        <Transition.Child
          as={Fragment}
          enter="duration-200 ease-in-out"
          enterFrom={sizeClasses.translation}
          enterTo="translate-x-[0]"
          leave="duration-200 ease-in-out"
          leaveFrom="translate-x-[0]"
          leaveTo={sizeClasses.translation}
        >
          <Dialog.Panel
            as="div"
            id="drawerPanel"
            className={classNames(
              'bg-soFresh-100 border-soFresh-140 fixed top-0 z-[200] flex h-screen flex-col overflow-hidden border',

              direction === 'left'
                ? `left-0 rounded-r-lg`
                : `right-0 rounded-l-lg`,
              `${sizeClasses.width} ${sizeClasses.maxWidth}`
            )}
          >
            {children}
          </Dialog.Panel>
        </Transition.Child>
        {/* NOTE: Not using the useOutsideClick hook in order to prevent the bug detailed in BAC-220 */}
        <Transition.Child
          enter="ease-linear"
          enterFrom="opacity-0"
          enterTo="opacity-50"
          leave="ease-linear"
          leaveFrom="opacity-50"
          leaveTo="opacity-0"
        >
          <Button
            id="drawerOverlay"
            className="pointer-events-auto fixed inset-0 z-40 h-screen w-screen cursor-default bg-black opacity-50"
            onClick={onCloseDrawer}
          />
        </Transition.Child>
      </Dialog>
    </Transition.Root>
  );
}
